<template>
  <div class="occupancy-summary-container">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".occupancy-summary-container"
                @cancel="closeHelp">
      <occupancy-summary-help :openSection="openHelpSection"></occupancy-summary-help>
    </res-dialog>
    <div class="grid-controls">
      <div class="historicals-controls">
        <res-radio-group label="Historicals"
                         :value="historicalDataYearsDisplayed"
                         :options="historicalRadioOptions"
                         @input="historicalDataYearsDisplayedChanged">
        </res-radio-group>
      </div>
      <div class="row-view-controls">
        <res-radio-group label="Row View"
                         :value="selectedViewType"
                         :options="viewOptions"
                         @input="selectedViewTypeChanged">
        </res-radio-group>
      </div>
      <div class="add-columns-controls">
        <span class="add-columns-label">Add Columns</span>
        <v-checkbox class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="YoY Variance $"
                    v-model="showVarianceAmountColumn"
                    @change="toggleVarianceAmountColumnVisibility">
        </v-checkbox>
        <v-checkbox class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="YoY Variance %"
                    v-model="showVariancePercentageColumn"
                    @change="toggleVariancePercentageColumnVisibility">
        </v-checkbox>
      </div>
      <div class="help-controls" @click="openHelp(null)">
        <span class="help-label">Help</span>
        <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
      </div>
    </div>
    <ag-grid-vue style="width: 100%;"
                 class="ag-theme-material"
                 :gridOptions="gridOptions"
                 :columnDefs="currentHeaders"
                 :rowData="currentData">
    </ag-grid-vue>
    <span v-if="estimatedFutureHistoricalBasis && historicalDataYearsDisplayed > 0" class="future-historical-indicator mb-5" v-show=false>*Indicates Estimated Future Historicals are based on {{estimatedFutureHistoricalBasis}}</span>
  </div>
</template>

<script>
  // View Types
  const DETAIL_LEVEL_VIEW = 0;
  const SUMMARY_LEVEL_VIEW = 1;

  // Row Types
  const GROSS_POTENTIAL_RENT = 1;
  const GROSS_POTENTIAL_RENT_HISTORICAL = 2;
  const LOSS_GAIN_TO_LEASE = 3;
  const LOSS_GAIN_TO_LEASE_HISTORICAL = 4;
  const VACANCY_LOSS = 5;
  const VACANCY_LOSS_HISTORICAL = 6;
  const MAD_VACANCY_LOSS = 7;
  const MAD_VACANCY_LOSS_HISTORICAL = 8;
  const RENTAL_DEDUCTIONS = 9;
  const RENTAL_DEDUCTIONS_HISTORICAL = 10;
  const BAD_DEBT = 11;
  const BAD_DEBT_HISTORICAL = 12;
  const NET_RENTAL_INCOME = 13;
  const NET_RENTAL_INCOME_HISTORICAL = 14;
  const AVERAGE_MARKET_RENT = 15;
  const AVERAGE_MARKET_RENT_HISTORICAL = 16;
  const AMR_PER_SQFT = 17;
  const AMR_PER_SQFT_HISTORICAL = 18;
  const PHYSICAL_OCCUPANCY = 19;
  const PHYSICAL_OCCUPANCY_HISTORICAL = 20;
  const ECONOMIC_OCCUPANCY = 21;
  const ECONOMIC_OCCUPANCY_HISTORICAL = 22;
  const ENDING_OCCUPIED_UNITS = 23;
  const ENDING_OCCUPIED_UNITS_HISTORICAL = 24;
  const ENDING_VACANT_UNITS = 25;
  const ENDING_VACANT_UNITS_HISTORICAL = 26;
  const TOTAL_UNITS = 27;
  const TOTAL_UNITS_HISTORICAL = 28;

  // Group Tags
  const GROSS_POTENTIAL_RENT_TAG = 'gpr';
  const LOSS_GAIN_TO_LEASE_TAG = 'loss-gain-lease';
  const VACANCY_LOSS_TAG = 'vacancy-loss';
  const MAD_VACANCY_LOSS_TAG = 'mad-vacancy-loss';
  const RENTAL_DEDUCTIONS_TAG = 'rental-deductions';
  const BAD_DEBT_TAG = 'bad-debt';
  const NET_RENTAL_INCOME_TAG = 'net-rental-income';
  const AVERAGE_MARKET_RENT_TAG = 'ave-market-rent';
  const AMR_PER_SQFT_TAG = 'ave-market-rent-sq-ft';
  const PHYSICAL_OCCUPANCY_TAG = 'physical-occupancy';
  const ECONOMIC_OCCUPANCY_TAG = 'economic-occupancy';
  const ENDING_OCCUPIED_UNITS_TAG = 'ending-occupied-units';
  const ENDING_VACANT_UNITS_TAG = 'ending-vacant-units';
  const TOTAL_UNITS_TAG = 'total-units';

  import YearGrid from '@/mixins/YearGrid';
  import HelpableMixin from '@/mixins/Helpable.js';

  import ResRadioGroup from "@/components/base/ResRadioGroup";
  import { AgGridVue } from "ag-grid-vue";
  import GroupedRowButton from '@/components/base/ag-grid/GroupedRowButton';
  import ConditionalIconCellRenderer from '@/components/base/ag-grid/ConditionalIconCellRenderer';
  import LabelCellRenderer from '@/components/base/ag-grid/LabelCellRenderer';

  import ResDialog from '@/components/base/ResDialog.vue';
  import OccupancySummaryHelp from '@/components/help/OccupancySummaryHelp.vue';

  import budgetService from '@/services/budget';
  import occupancyService from '@/services/occupancy';

  import {Constants} from '@/utilities/constants/index.js';
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'OccupancySummary',
    mixins: [
      YearGrid,
      HelpableMixin
    ],
    components: {
      ResRadioGroup,
      AgGridVue,
      OccupancySummaryHelp,
      ResDialog,
      /* eslint-disable vue/no-unused-components */
      GroupedRowButton,                 // used by AG Grid
      ConditionalIconCellRenderer,      // used by AG Grid
      LabelCellRenderer                 // used by AG Grid
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: { 
        required: true
      }
    },
    data: function () {
      return {
        historicalRadioOptions: [
          { label: 'None', value: 0 },
          { label: '1 Year', value: 1 },
          { label: '2 Years', value: 2 },
          { label: '3 Years', value: 3 }
        ],
        historicalDataYearsDisplayed: 0, // default to hiding history
        historicalsLoaded: false,
        viewOptions: [
          { label: 'Detailed', value: DETAIL_LEVEL_VIEW },
          { label: 'Summary', value: SUMMARY_LEVEL_VIEW }
        ],
        selectedViewType: DETAIL_LEVEL_VIEW, // default to detail view
        fiscalYear: null,
        fiscalYearStartMonth: null,
        availableColumns: [],
        gridOptions: {
          getRowNodeId: this.getRowIdentifier,
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          alignedGrids: [],
          getRowClass: this.getRowClass,
          isExternalFilterPresent: () => this.shouldRowsBeFiltered,
          doesExternalFilterPass: this.shouldShowRow,
          frameworkComponents: {
            'ConditionalIconCellRenderer': 'ConditionalIconCellRenderer',
            'LabelCellRenderer': 'LabelCellRenderer'
          }
        },
        occupancyData: [],
        showVarianceAmountColumn: false, // default to hiding variance amount column
        showVariancePercentageColumn: false, // default to hiding variance percentage column
        estimateFutureHistoricalOptionMonthOption: 0,
        estimateFutureHistoricalsOccupancyWorkbooksOption: 1,
        startingPeriodIndexToEstimate: 0,
        estimatedFutureHistoricalBasis: null
      };
    },
    methods: {
      ...mapActions(['changeLoading']),
      // DOM load and update logic
      async load() {
        let t = this;

        t.showGridLoadingOverlay(t.gridOptions);

        await t.loadGridConfiguration();
        await t.loadOccupancySummaryData(false);

        t.sizeGridColumns(t.gridOptions);
        t.hideGridLoadingOverlay(t.gridOptions);
      },
      async loadGridConfiguration() {
        let t = this;

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        t.estimateFutureHistoricalOptionMonthOption = settings.estimateFutureHistoricalOptionMonthOption;
        t.estimateFutureHistoricalsOccupancyWorkbooksOption = settings.estimateFutureHistoricalsOccupancyWorkbooksOption;
        if (t.fiscalYearStartMonth == 1)
        {                       
          t.startingPeriodIndexToEstimate = t.estimateFutureHistoricalOptionMonthOption - 1;
        }
        else
        {                      
          if (t.fiscalYearStartMonth > t.estimateFutureHistoricalOptionMonthOption)
          {
            t.startingPeriodIndexToEstimate = 12 - t.fiscalYearStartMonth + t.estimateFutureHistoricalOptionMonthOption;
          }
          else
          {
            t.startingPeriodIndexToEstimate = t.estimateFutureHistoricalOptionMonthOption - t.fiscalYearStartMonth;
          }
        }
        switch (settings.estimateFutureHistoricalsOccupancyWorkbooksOption) {
          case 1:
            this.estimatedFutureHistoricalBasis = 'annualizing current year';
            break;
          case 2:
            this.estimatedFutureHistoricalBasis = 'using last year actuals';
            break;
          case 3:
            this.estimatedFutureHistoricalBasis = null;
            break;         
        }
        // setup column headers
        let months = t.getFiscalYearMonths(t.fiscalYearStartMonth);
        t.availableColumns = [
          { 
            headerName: "", field: "isGroupCollapsed", width: 16, pinned: 'left', suppressSizeToFit: true,
            cellStyle: { margin: 0, padding: 0 },
            cellRendererFramework: 'GroupedRowButton',
            cellRendererParams: {
              show: function(params) { 
                return params.data.isGroupParent && t.historicalDataYearsDisplayed > 0;
              },
              allCollapsed: function(params) {
                return params.data.isGroupCollapsed || t.historicalDataYearsDisplayed <= 0;
              },
              onClick: function(params) {
                if (t.historicalDataYearsDisplayed <= 0) {
                  return; // nothing to really do when historicals are not being shown
                }
                if (params.data.isGroupParent) {
                  let show = !params.data.isGroupCollapsed; // toggle
                  params.api.forEachNode(node => {
                    if (node.data.groupTag == params.data.groupTag) {
                      node.setDataValue('isGroupCollapsed', show);
                    }
                  });
                  t.refreshDataFilter();
                }
              }
            }
          },
          { 
            headerName: "", field: "label", width: 240, pinned: 'left', suppressSizeToFit: true,
            cellRendererSelector: t.selectLabelCellRenderer,
            cellStyle: function(params) {
                var style = { 'margin-left': 0, 'margin-top': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'right' };
                if (params.data.subLabel) {
                  style['line-height'] = 'normal'; 
                }
                return style;
            }
          },
          { 
            headerName: "", field: "year", width: 60, pinned: 'left', suppressSizeToFit: true, 
            cellStyle: { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold' } 
          },
          { headerName: months[0], field: "periodIndex00Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[1], field: "periodIndex01Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[2], field: "periodIndex02Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[3], field: "periodIndex03Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[4], field: "periodIndex04Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[5], field: "periodIndex05Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[6], field: "periodIndex06Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[7], field: "periodIndex07Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[8], field: "periodIndex08Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[9], field: "periodIndex09Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[10], field: "periodIndex10Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: months[11], field: "periodIndex11Amount", width: 96, valueFormatter: t.cellFormatter },
          { headerName: "TOTAL/AVG", field: "totalOrAverage", width: 110, pinned: 'right', valueFormatter: t.cellFormatter },
          { headerName: "Var. $", field: "varAmount", width: 96, pinned: 'right', valueFormatter: t.varianceAmountCellFormatter, hide: !t.showVarianceAmountColumn },
          { headerName: "Var. %", field: "varPercent", width: 96, pinned: 'right', valueFormatter: t.variancePercentageCellFormatter, hide: !t.showVariancePercentageColumn }
        ];
      },
      async loadOccupancySummaryData(includeHistoricals) {
        let t = this;

        let data = await occupancyService.getOccupancySummary(t.budgetIterationId, includeHistoricals);
        
        t.occupancyData = [];
        t.occupancyData.push(t.initRow(GROSS_POTENTIAL_RENT, data.grossPotentialRent, SUMMARY_LEVEL_VIEW));
        if (data.grossPotentialRent.hasHistoricals) {
          (data.grossPotentialRent.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(GROSS_POTENTIAL_RENT_HISTORICAL, item, SUMMARY_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(LOSS_GAIN_TO_LEASE, data.lossGainToLease, DETAIL_LEVEL_VIEW));
        if (data.lossGainToLease.hasHistoricals) {
          (data.lossGainToLease.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(LOSS_GAIN_TO_LEASE_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(VACANCY_LOSS, data.vacancyLoss, DETAIL_LEVEL_VIEW));
        if (data.vacancyLoss.hasHistoricals) {
          (data.vacancyLoss.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(VACANCY_LOSS_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(MAD_VACANCY_LOSS, data.vacancyLossModelAdminDown, DETAIL_LEVEL_VIEW));
        if (data.vacancyLossModelAdminDown.hasHistoricals) {
          (data.vacancyLossModelAdminDown.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(MAD_VACANCY_LOSS_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(RENTAL_DEDUCTIONS, data.rentalDeductions, DETAIL_LEVEL_VIEW));
        if (data.rentalDeductions.hasHistoricals) {
          (data.rentalDeductions.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(RENTAL_DEDUCTIONS_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(BAD_DEBT, data.badDebt, DETAIL_LEVEL_VIEW));
        if (data.badDebt.hasHistoricals) {
          (data.badDebt.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(BAD_DEBT_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(NET_RENTAL_INCOME, data.netRentalIncome, SUMMARY_LEVEL_VIEW));
        if (data.netRentalIncome.hasHistoricals) {
          (data.netRentalIncome.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(NET_RENTAL_INCOME_HISTORICAL, item, SUMMARY_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(AVERAGE_MARKET_RENT, data.averageMarketRent, DETAIL_LEVEL_VIEW));
        if (data.averageMarketRent.hasHistoricals) {
          (data.averageMarketRent.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(AVERAGE_MARKET_RENT_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(AMR_PER_SQFT, data.averageMarketRentPerSquareFoot, DETAIL_LEVEL_VIEW));
        if (data.averageMarketRentPerSquareFoot.hasHistoricals) {
          (data.averageMarketRentPerSquareFoot.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(AMR_PER_SQFT_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(PHYSICAL_OCCUPANCY, data.physicalOccupancy, SUMMARY_LEVEL_VIEW));
        if (data.physicalOccupancy.hasHistoricals) {
          (data.physicalOccupancy.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(PHYSICAL_OCCUPANCY_HISTORICAL, item, SUMMARY_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(ECONOMIC_OCCUPANCY, data.economicOccupancy, SUMMARY_LEVEL_VIEW));
        if (data.economicOccupancy.hasHistoricals) {
          (data.economicOccupancy.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(ECONOMIC_OCCUPANCY_HISTORICAL, item, SUMMARY_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(ENDING_OCCUPIED_UNITS, data.endingOccupiedUnits, DETAIL_LEVEL_VIEW));
        if (data.endingOccupiedUnits.hasHistoricals) {
          (data.endingOccupiedUnits.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(ENDING_OCCUPIED_UNITS_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(ENDING_VACANT_UNITS, data.endingVacantUnits, DETAIL_LEVEL_VIEW));
        if (data.endingVacantUnits.hasHistoricals) {
          (data.endingVacantUnits.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(ENDING_VACANT_UNITS_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
        t.occupancyData.push(t.initRow(TOTAL_UNITS, data.totalUnits, DETAIL_LEVEL_VIEW));
        if (data.totalUnits.hasHistoricals) {
          (data.totalUnits.historicals).forEach((item) => {
            t.occupancyData.push(t.initRow(TOTAL_UNITS_HISTORICAL, item, DETAIL_LEVEL_VIEW));
          });
        }
      },
      async refreshOccupancySummaryData() {
        let t = this;

        t.showGridLoadingOverlay(t.gridOptions);

        let data = await occupancyService.getOccupancySummary(t.budgetIterationId, false);

        t.setRowValues(GROSS_POTENTIAL_RENT, data.grossPotentialRent);
        t.setRowValues(LOSS_GAIN_TO_LEASE, data.lossGainToLease);
        t.setRowValues(VACANCY_LOSS, data.vacancyLoss);
        t.setRowValues(MAD_VACANCY_LOSS, data.vacancyLossModelAdminDown);
        t.setRowValues(RENTAL_DEDUCTIONS, data.rentalDeductions);
        t.setRowValues(BAD_DEBT, data.badDebt);
        t.setRowValues(NET_RENTAL_INCOME, data.netRentalIncome);
        t.setRowValues(AVERAGE_MARKET_RENT, data.averageMarketRent);
        t.setRowValues(AMR_PER_SQFT, data.averageMarketRentPerSquareFoot);
        t.setRowValues(PHYSICAL_OCCUPANCY, data.physicalOccupancy);
        t.setRowValues(ECONOMIC_OCCUPANCY, data.economicOccupancy);
        t.setRowValues(ENDING_OCCUPIED_UNITS, data.endingOccupiedUnits);
        t.setRowValues(ENDING_VACANT_UNITS, data.endingVacantUnits);
        t.setRowValues(TOTAL_UNITS, data.totalUnits);

        t.hideGridLoadingOverlay(t.gridOptions);
      },
      async loadHistoricals() {
        let t = this;

        if (t.historicalsLoaded == false) {
          t.showGridLoadingOverlay(t.gridOptions);

          await t.loadOccupancySummaryData(true);

          t.sizeGridColumns(t.gridOptions);
          t.hideGridLoadingOverlay(t.gridOptions);

          t.historicalsLoaded = true;
        }
      },
      // row initialization logic
      initRow(rowType, data, viewType) {
        return {
          // identifier
          rowType: rowType,

          // displayed columns
          label: data.label,
          subLabel: data.subLabel,
          year: data.year,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,
          priorYearTotal: data.priorYearTotal,
          totalOrAverage: this.getTotalOrAverage(rowType, data),
          varAmount: data.varianceAmount,
          varPercent: data.variancePercentage,

          // grouping metadata
          isGroupParent: data.hasHistoricals && this.isGroupParentRow(rowType),
          groupTag: this.getRowGroupTag(rowType),
          isGroupCollapsed: false,
          viewType: viewType,

          // historical metadata
          hasHistoricalRecords: data.hasHistoricals,
          historicalRecordAge: this.fiscalYear - data.year
        }
      },
      getRowIdentifier(data) {
        if (this.isNonHistoricalRow(data.rowType)) {
          return data.rowType.toString();
        }
        return this.getHistoricalRowIdentifier(data.rowType, data.year);
      },
      getHistoricalRowIdentifier(rowType, year) {
        return `${rowType}-${year}`;
      },
      displaysAverageAsTotal(rowType) {
        switch(rowType) {
          case AVERAGE_MARKET_RENT:
          case AVERAGE_MARKET_RENT_HISTORICAL:
          case AMR_PER_SQFT:
          case AMR_PER_SQFT_HISTORICAL:
          case PHYSICAL_OCCUPANCY:
          case PHYSICAL_OCCUPANCY_HISTORICAL:
          case ECONOMIC_OCCUPANCY:
          case ECONOMIC_OCCUPANCY_HISTORICAL:
          case ENDING_OCCUPIED_UNITS:
          case ENDING_OCCUPIED_UNITS_HISTORICAL:
          case ENDING_VACANT_UNITS:
          case ENDING_VACANT_UNITS_HISTORICAL:
          case TOTAL_UNITS:
          case TOTAL_UNITS_HISTORICAL:
            return true;
          default:
            return false;
        }
      },
      getTotalOrAverage(rowType, data) {
        if (this.displaysAverageAsTotal(rowType)) {
          return this.roundsTotalOrAverage(rowType) ? this.parseInteger(data.average) : this.parseCurrency(data.average);  
        } 
        return this.roundsTotalOrAverage(rowType) ? this.parseInteger(data.total) : this.parseCurrency(data.total);
      },
      roundsTotalOrAverage(rowType) {
        switch(rowType) {
          case ENDING_OCCUPIED_UNITS:
          case ENDING_OCCUPIED_UNITS_HISTORICAL:
          case ENDING_VACANT_UNITS:
          case ENDING_VACANT_UNITS_HISTORICAL:
          case TOTAL_UNITS:
          case TOTAL_UNITS_HISTORICAL:
            return true;
          default:
            return false;
        }
      },
      selectLabelCellRenderer(params) {
        if (params.data.rowType == ECONOMIC_OCCUPANCY) {
          return {
            component: 'ConditionalIconCellRenderer',
            params: {
              showIcon: true,
              iconCode: 'mdi-help-circle',
              iconColor: 'var(--v-resBlue-darken1)',
              tooltip: 'Based on GL account balances using the formula found in Admin > Settings > Reports > Economic Occupancy.',
              tooltipOnTop: true,
              tooltipMaxWidth: '400px',
              tooltipNudgeRight: 190
            }
          }
        }

        if (params.data.subLabel) {
          return {
            component: 'LabelCellRenderer',
            params: {
              label: params.data.label,
              subLabel: params.data.subLabel
            }
          }
        }

        return null; // use default renderer
      },
      getAssociatedHistoricalRowType(rowType) {
        switch(rowType) {
          case GROSS_POTENTIAL_RENT:
            return GROSS_POTENTIAL_RENT_HISTORICAL;
          case LOSS_GAIN_TO_LEASE:
            return LOSS_GAIN_TO_LEASE_HISTORICAL;
          case VACANCY_LOSS:
            return VACANCY_LOSS_HISTORICAL;
          case MAD_VACANCY_LOSS:
            return MAD_VACANCY_LOSS_HISTORICAL;
          case RENTAL_DEDUCTIONS:
            return RENTAL_DEDUCTIONS_HISTORICAL;
          case BAD_DEBT:
            return BAD_DEBT_HISTORICAL;
          case NET_RENTAL_INCOME:
            return NET_RENTAL_INCOME_HISTORICAL;
          case AVERAGE_MARKET_RENT:
            return AVERAGE_MARKET_RENT_HISTORICAL;
          case AMR_PER_SQFT:
            return AMR_PER_SQFT_HISTORICAL;
          case PHYSICAL_OCCUPANCY:
            return PHYSICAL_OCCUPANCY_HISTORICAL;
          case ECONOMIC_OCCUPANCY:
            return ECONOMIC_OCCUPANCY_HISTORICAL;
          case ENDING_OCCUPIED_UNITS:
            return ENDING_OCCUPIED_UNITS_HISTORICAL;
          case ENDING_VACANT_UNITS:
            return ENDING_VACANT_UNITS_HISTORICAL;
          case TOTAL_UNITS:
            return TOTAL_UNITS_HISTORICAL;
          default:
            return null;
        }
      },
      isNonHistoricalRow(rowType) {
        switch(rowType) {
          case GROSS_POTENTIAL_RENT:
          case LOSS_GAIN_TO_LEASE:
          case VACANCY_LOSS:
          case MAD_VACANCY_LOSS:
          case RENTAL_DEDUCTIONS:
          case BAD_DEBT:
          case NET_RENTAL_INCOME:
          case AVERAGE_MARKET_RENT:
          case AMR_PER_SQFT:
          case PHYSICAL_OCCUPANCY:
          case ECONOMIC_OCCUPANCY:
          case ENDING_OCCUPIED_UNITS:
          case ENDING_VACANT_UNITS:
          case TOTAL_UNITS:
            return true;
          default:
            return false;
        }
      },
      isGroupParentRow(rowType) {
        return this.isNonHistoricalRow(rowType);
      },
      getRowGroupTag(rowType) {
        switch(rowType) {
          case GROSS_POTENTIAL_RENT:
          case GROSS_POTENTIAL_RENT_HISTORICAL:
            return GROSS_POTENTIAL_RENT_TAG;
          case LOSS_GAIN_TO_LEASE:
          case LOSS_GAIN_TO_LEASE_HISTORICAL:
            return LOSS_GAIN_TO_LEASE_TAG;
          case VACANCY_LOSS:
          case VACANCY_LOSS_HISTORICAL:
            return VACANCY_LOSS_TAG;  
          case MAD_VACANCY_LOSS:
          case MAD_VACANCY_LOSS_HISTORICAL:
            return MAD_VACANCY_LOSS_TAG;  
          case RENTAL_DEDUCTIONS:
          case RENTAL_DEDUCTIONS_HISTORICAL:
            return RENTAL_DEDUCTIONS_TAG;  
          case BAD_DEBT:
          case BAD_DEBT_HISTORICAL:
            return BAD_DEBT_TAG;  
          case NET_RENTAL_INCOME:
          case NET_RENTAL_INCOME_HISTORICAL:
            return NET_RENTAL_INCOME_TAG;  
          case AVERAGE_MARKET_RENT:
          case AVERAGE_MARKET_RENT_HISTORICAL:
            return AVERAGE_MARKET_RENT_TAG;  
          case AMR_PER_SQFT:
          case AMR_PER_SQFT_HISTORICAL:
            return AMR_PER_SQFT_TAG;  
          case PHYSICAL_OCCUPANCY:
         case PHYSICAL_OCCUPANCY_HISTORICAL:
            return PHYSICAL_OCCUPANCY_TAG;  
          case ECONOMIC_OCCUPANCY:
          case ECONOMIC_OCCUPANCY_HISTORICAL:
            return ECONOMIC_OCCUPANCY_TAG;  
          case ENDING_OCCUPIED_UNITS:
          case ENDING_OCCUPIED_UNITS_HISTORICAL:
            return ENDING_OCCUPIED_UNITS_TAG;  
          case ENDING_VACANT_UNITS:
          case ENDING_VACANT_UNITS_HISTORICAL:
            return ENDING_VACANT_UNITS_TAG;  
          case TOTAL_UNITS:
          case TOTAL_UNITS_HISTORICAL:
            return TOTAL_UNITS_TAG;
          default:
            return null;
        }
      },
      getRowClass(params) {
        let classes = ['readonly-row'];
        switch(params.data.rowType) { 
          case GROSS_POTENTIAL_RENT:
          case LOSS_GAIN_TO_LEASE:
          case NET_RENTAL_INCOME:
          case AVERAGE_MARKET_RENT:
          case PHYSICAL_OCCUPANCY:
          case TOTAL_UNITS:
            classes.push('top-lined-row');
            break;
          case GROSS_POTENTIAL_RENT_HISTORICAL:
          case LOSS_GAIN_TO_LEASE_HISTORICAL:
          case VACANCY_LOSS_HISTORICAL:
          case MAD_VACANCY_LOSS_HISTORICAL:
          case RENTAL_DEDUCTIONS_HISTORICAL:
          case BAD_DEBT_HISTORICAL:
          case NET_RENTAL_INCOME_HISTORICAL:
          case AVERAGE_MARKET_RENT_HISTORICAL:
          case AMR_PER_SQFT_HISTORICAL:
          case PHYSICAL_OCCUPANCY_HISTORICAL:
          case ECONOMIC_OCCUPANCY_HISTORICAL:
          case ENDING_OCCUPIED_UNITS_HISTORICAL:
          case ENDING_VACANT_UNITS_HISTORICAL:
          case TOTAL_UNITS_HISTORICAL:
            classes.push('historical');
            break;
          default:
            break;
        }        
        return classes;
      },
      shouldRowsBeFiltered() {
        return true;
      },
      shouldShowRow(node) {
        return (node.data.isGroupParent ||
        (!node.data.isGroupCollapsed && node.data.historicalRecordAge <= this.historicalDataYearsDisplayed)) &&
        (node.data.viewType >= this.selectedViewType);
      },
      async historicalDataYearsDisplayedChanged(yearsDisplayed) {
        if (yearsDisplayed > 0) {
          await this.loadHistoricals();
        }
        this.historicalDataYearsDisplayed = yearsDisplayed;
        this.refreshDataFilter();
      },
      selectedViewTypeChanged(rowType) {
        this.selectedViewType = rowType;
        this.refreshDataFilter();
      },
      refreshDataFilter() {
        this.gridOptions.api.onFilterChanged();
      },
      async toggleVarianceAmountColumnVisibility() {
        if (this.showVarianceAmountColumn) {
          await this.loadHistoricals();
        }
        this.gridOptions.columnApi.setColumnVisible('varAmount', this.showVarianceAmountColumn);
        this.sizeGridColumns(this.gridOptions);
      },
      async toggleVariancePercentageColumnVisibility() {
        if (this.showVariancePercentageColumn) {
          await this.loadHistoricals();
        }
        this.gridOptions.columnApi.setColumnVisible('varPercent', this.showVariancePercentageColumn);
        this.sizeGridColumns(this.gridOptions);
      },
      // row update logic
      setRowValues(rowIdentifier, data) {
        let row = this.gridOptions.api.getRowNode(rowIdentifier);
        if (row) {
          row.setDataValue('periodIndex00Amount', data.periodIndex00.amount);
          row.setDataValue('periodIndex01Amount', data.periodIndex01.amount);
          row.setDataValue('periodIndex02Amount', data.periodIndex02.amount);
          row.setDataValue('periodIndex03Amount', data.periodIndex03.amount);
          row.setDataValue('periodIndex04Amount', data.periodIndex04.amount);
          row.setDataValue('periodIndex05Amount', data.periodIndex05.amount);
          row.setDataValue('periodIndex06Amount', data.periodIndex06.amount);
          row.setDataValue('periodIndex07Amount', data.periodIndex07.amount);
          row.setDataValue('periodIndex08Amount', data.periodIndex08.amount);
          row.setDataValue('periodIndex09Amount', data.periodIndex09.amount);
          row.setDataValue('periodIndex10Amount', data.periodIndex10.amount);
          row.setDataValue('periodIndex11Amount', data.periodIndex11.amount);
          let val = this.displaysAverageAsTotal(row.data.rowType) ? 
            (this.roundsTotalOrAverage(row.data.rowType) ? this.parseInteger(data.average) : this.parseCurrency(data.average)) : 
            data.total;
          row.setDataValue('totalOrAverage', val);

          let priorYearRowIdentifier = this.getHistoricalRowIdentifier(this.getAssociatedHistoricalRowType(row.data.rowType), row.data.year - 1);
          let priorYearRow = this.gridOptions.api.getRowNode(priorYearRowIdentifier);
          if (priorYearRow) {
            let varianceAmount = val - priorYearRow.data.totalOrAverage;
            let variancePercentage = val > 0 || val < 0 ? (varianceAmount / val) * 100 : null;
            row.setDataValue('varAmount', varianceAmount);
            row.setDataValue('varPercent', variancePercentage);
          }
        }
      },
      cellFormatter(params) {          
        let metaFormat;
        switch (params.data.rowType) {
          // percentages
          case PHYSICAL_OCCUPANCY:
          case PHYSICAL_OCCUPANCY_HISTORICAL:
          case ECONOMIC_OCCUPANCY:
          case ECONOMIC_OCCUPANCY_HISTORICAL:
            metaFormat = this.formatPercentage(params.value, true, true);
            break;
          // dollar amounts (rounded)
          case GROSS_POTENTIAL_RENT:
          case GROSS_POTENTIAL_RENT_HISTORICAL:
          case LOSS_GAIN_TO_LEASE:
          case LOSS_GAIN_TO_LEASE_HISTORICAL:
          case VACANCY_LOSS:
          case VACANCY_LOSS_HISTORICAL:
          case MAD_VACANCY_LOSS:
          case MAD_VACANCY_LOSS_HISTORICAL:
          case RENTAL_DEDUCTIONS:
          case RENTAL_DEDUCTIONS_HISTORICAL:
          case BAD_DEBT:
          case BAD_DEBT_HISTORICAL:
          case NET_RENTAL_INCOME:
          case NET_RENTAL_INCOME_HISTORICAL:
          case AVERAGE_MARKET_RENT:
          case AVERAGE_MARKET_RENT_HISTORICAL:
            metaFormat = this.formatCurrency(params.value, true, true);
            break;
          // dollar amounts (2 decimal places)
          case AMR_PER_SQFT:
          case AMR_PER_SQFT_HISTORICAL:
            metaFormat = this.formatCurrency(params.value, false, true);
            break;
          // integers
          case ENDING_OCCUPIED_UNITS:
          case ENDING_OCCUPIED_UNITS_HISTORICAL:
          case ENDING_VACANT_UNITS:
          case ENDING_VACANT_UNITS_HISTORICAL:
          case TOTAL_UNITS:
          case TOTAL_UNITS_HISTORICAL:
            metaFormat = this.formatInteger(params.value);
            break;
          default:
            metaFormat = params.value?.toString();        
        }
        let periodIndex = +params.column.colId.replace('periodIndex', '').replace('Amount', '');
        let prefix = (( this.estimateFutureHistoricalsOccupancyWorkbooksOption != 3) && (params.data.year == this.fiscalYear -1) && (periodIndex >= this.startingPeriodIndexToEstimate)) ? '' : ''
        return prefix + metaFormat;
      },
      varianceAmountCellFormatter(params) {
        switch (params.data.rowType) {
          // non-applicable rows
          case PHYSICAL_OCCUPANCY:
          case PHYSICAL_OCCUPANCY_HISTORICAL:
          case ECONOMIC_OCCUPANCY:
          case ECONOMIC_OCCUPANCY_HISTORICAL:
          case ENDING_OCCUPIED_UNITS:
          case ENDING_OCCUPIED_UNITS_HISTORICAL:
          case ENDING_VACANT_UNITS:
          case ENDING_VACANT_UNITS_HISTORICAL:
          case TOTAL_UNITS:
          case TOTAL_UNITS_HISTORICAL:
            return Constants.DASHED_CELL_VALUE;
          // dollar amounts (rounded)
          case GROSS_POTENTIAL_RENT:
          case GROSS_POTENTIAL_RENT_HISTORICAL:
          case LOSS_GAIN_TO_LEASE:
          case LOSS_GAIN_TO_LEASE_HISTORICAL:
          case VACANCY_LOSS:
          case VACANCY_LOSS_HISTORICAL:
          case MAD_VACANCY_LOSS:
          case MAD_VACANCY_LOSS_HISTORICAL:
          case RENTAL_DEDUCTIONS:
          case RENTAL_DEDUCTIONS_HISTORICAL:
          case BAD_DEBT:
          case BAD_DEBT_HISTORICAL:
          case NET_RENTAL_INCOME:
          case NET_RENTAL_INCOME_HISTORICAL:
          case AVERAGE_MARKET_RENT:
          case AVERAGE_MARKET_RENT_HISTORICAL:
            return this.formatCurrency(params.value, true, true);
          // dollar amounts (2 decimal places)
          case AMR_PER_SQFT:
          case AMR_PER_SQFT_HISTORICAL:
            return this.formatCurrency(params.value, false, true);
          default:
            return params.value?.toString();
        }
      },
      variancePercentageCellFormatter(params) {
        switch (params.data.rowType) {
          // non-applicable rows
          case PHYSICAL_OCCUPANCY:
          case PHYSICAL_OCCUPANCY_HISTORICAL:
          case ECONOMIC_OCCUPANCY:
          case ECONOMIC_OCCUPANCY_HISTORICAL:
          case ENDING_OCCUPIED_UNITS:
          case ENDING_OCCUPIED_UNITS_HISTORICAL:
          case ENDING_VACANT_UNITS:
          case ENDING_VACANT_UNITS_HISTORICAL:
          case TOTAL_UNITS:
          case TOTAL_UNITS_HISTORICAL:
            return Constants.DASHED_CELL_VALUE;
          // dollar amounts (rounded)
          case GROSS_POTENTIAL_RENT:
          case GROSS_POTENTIAL_RENT_HISTORICAL:
          case LOSS_GAIN_TO_LEASE:
          case LOSS_GAIN_TO_LEASE_HISTORICAL:
          case VACANCY_LOSS:
          case VACANCY_LOSS_HISTORICAL:
          case MAD_VACANCY_LOSS:
          case MAD_VACANCY_LOSS_HISTORICAL:
          case RENTAL_DEDUCTIONS:
          case RENTAL_DEDUCTIONS_HISTORICAL:
          case BAD_DEBT:
          case BAD_DEBT_HISTORICAL:
          case NET_RENTAL_INCOME:
          case NET_RENTAL_INCOME_HISTORICAL:
          case AVERAGE_MARKET_RENT:
          case AVERAGE_MARKET_RENT_HISTORICAL:
          case AMR_PER_SQFT:
          case AMR_PER_SQFT_HISTORICAL:
            return this.formatPercentage(params.value, true, true);
          default:
            return params.value?.toString();
        }
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      currentData() {
        return this.occupancyData;
      },
      currentHeaders() { 
        return this.availableColumns;
      }
    },
    async mounted() {
      await this.load(true);
    },
    async created() {
      this.$eventHub.$on('budgetSettingsUpdated', this.load);
      this.$eventHub.$on('modelAdminDownEntriesUpdated', this.refreshOccupancySummaryData);
      this.$eventHub.$on('moveInMoveOutEntriesUpdated', this.refreshOccupancySummaryData);
      this.$eventHub.$on('actualRentEntriesUpdated', this.refreshOccupancySummaryData);
      this.$eventHub.$on('rentalDeductionEntriesUpdated', this.refreshOccupancySummaryData);
    }
  };
</script>

<style lang="scss" scoped>
.v-input--selection-controls::v-deep {
  margin: 0 !important;
  padding: 0 !important;
}
.grid-controls {
  height: 40px;
  margin-bottom: 14px;
}
.historicals-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
.row-view-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
.add-columns-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  vertical-align: top; 
  height: 100%;
}
.add-columns-label {
  vertical-align: top;
  display: inline-block;
  padding: 7px 8px 0 10px; 
  height: 100%;
}
.add-columns-option::v-deep {
  display: inline-block;
  .v-label {
    font-size: 13px !important;
    color: var(--v-resGrey-darken2);
    font-weight: bold;
    padding-right: 14px;
    padding-top: 8px;
  }
  .v-input--selection-controls__input {
    margin-top: 0;
    padding-top: 8px;
  }
  .v-messages {
    display: none;
  }
}
.help-controls {
  height: 100%;
  float: right;
  border: solid 1px var(--v-resGrey-lighten3);
  padding: 7px 6px 6px 6px;
  cursor: pointer !important;
}
.help-label {
  padding-right: 6px;
}
.future-historical-indicator {
    background-color: #e2e2e2;
    width: 100%;
    padding: 3px;
    padding-right: 10px;
    margin-top: -1px;
    position: relative;
    display: block;
    text-align: right;
  }
</style>
